import { useQuery } from "@tanstack/react-query";
import { FileWarning, Receipt } from "lucide-react";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Card, EmptyState, Skeleton } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { intlLocale, t, tn } from "@/lib/i18n";

const euro = (v: number) => new Intl.NumberFormat(intlLocale(), { style: "currency", currency: "EUR" }).format(v);

/** Offene Rechnungen (Link aus dem Banner „überfällige Rechnungen“) – Stand des stündlichen Abgleichs mit der Abrechnung */
export function OpenInvoicesPage() {
  const { tenantId } = useTenant();
  const q = useQuery({ queryKey: ["open-invoices", tenantId], queryFn: () => api.invoices.open(tenantId), retry: false });
  const forbidden = q.error instanceof ApiError && q.error.status === 403;
  const overdue = q.data?.invoices.filter((i) => i.overdue) ?? [];
  const sum = overdue.reduce((s, i) => s + i.open, 0);

  return (
    <div className="pb-16">
      <PageHeader title={t("Offene Rechnungen")} description={t("Rechnungen von Logiful, die noch nicht (vollständig) bezahlt sind. Bezahlungen erscheinen hier spätestens eine Stunde nach dem Zahlungseingang.")} />
      <div className="px-4 md:px-8 space-y-4">
        {q.isLoading ? (
          <Skeleton className="h-40" />
        ) : forbidden ? (
          <EmptyState icon={<Receipt className="size-5" />} title={t("Nicht freigegeben")}>{t("Rechnungen sehen bei euch nur die dafür freigegebenen Personen.")}</EmptyState>
        ) : !q.data?.invoices.length ? (
          <EmptyState icon={<Receipt className="size-5" />} title={t("Keine offenen Rechnungen")}>{t("Alles bezahlt – danke!")}</EmptyState>
        ) : (
          <>
            {overdue.length > 0 && (
              <Card className="p-4 flex items-start gap-3 border-danger/30 bg-danger-soft/40">
                <FileWarning className="size-5 text-danger mt-0.5" />
                <p className="text-[14px]">{tn(overdue.length, "{n} überfällige Rechnung über {sum}", "{n} überfällige Rechnungen über {sum}", { sum: euro(sum) })}</p>
              </Card>
            )}
            <Card className="overflow-hidden">
              <table className="w-full text-[13.5px]">
                <thead className="bg-surface-2/50 text-[12px] text-fg-subtle">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">{t("Rechnung")}</th>
                    <th className="px-4 py-2 text-left font-medium">{t("Datum")}</th>
                    <th className="px-4 py-2 text-right font-medium">{t("Betrag")}</th>
                    <th className="px-4 py-2 text-right font-medium">{t("Offen")}</th>
                    <th className="px-4 py-2 text-left font-medium">{t("Stand")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {q.data.invoices.map((i) => (
                    <tr key={i.id}>
                      <td className="px-4 py-2.5 font-mono">{i.number ?? "—"}</td>
                      <td className="px-4 py-2.5 tabular-nums">{new Date(`${i.date}T12:00:00`).toLocaleDateString(intlLocale())}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{euro(i.gross)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-medium">{euro(i.open)}</td>
                      <td className="px-4 py-2.5">
                        {i.debitPending ? <Badge tone="info">{t("Lastschrift läuft")}</Badge> : i.overdue ? <Badge tone="danger">{t("überfällig seit {d} Tagen", { d: i.days })}</Badge> : <Badge tone="neutral">{t("offen")}</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
            {q.data.checkedAt && <p className="text-[12px] text-fg-subtle">{t("Stand: {d}", { d: fmtDateTime(q.data.checkedAt) })} · {t("Fragen zu Rechnungen? Einfach auf die Rechnungsmail antworten.")}</p>}
          </>
        )}
      </div>
    </div>
  );
}
